import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, message, Tooltip, Dropdown } from 'antd';
import { DownloadOutlined, FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import { 
  exportToPNG, 
  exportToPDF, 
  exportGoogleChartToPNG,
  exportGoogleChartToPDF,
  exportSVGDirectToPNG,
  exportSVGDirectToPDF,
  generateFileName, 
  validateElementForExport,
  exportConfigs 
} from '../../utils/exportChart';

const ExportChartButtons = ({ 
  elementRef,
  fileName = 'sankey-chart',
  chartType = 'sankey',
  title = '',
  analysisType = '',
  analysisName = '',
  showLabels = true,
  size = 'middle',
  style = {}
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState(null);

  const handleExport = async (format) => {
    if (!elementRef?.current) {
      message.error('No se encontró la gráfica para exportar');
      return;
    }

    // Validar elemento
    const validation = validateElementForExport(elementRef.current);
    if (!validation.valid) {
      message.error(validation.message);
      return;
    }

    setIsExporting(true);
    setExportingType(format);

    try {
      // Esperar un poco más para asegurar renderizado completo
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Generar nombre de archivo único
      const uniqueFileName = generateFileName(fileName, analysisType, analysisName);
      
      // Obtener configuración para el tipo de gráfica
      const config = exportConfigs[chartType] || exportConfigs.sankey;
      
      let result;
      
      if (format === 'png') {
        console.log('=== INICIANDO EXPORTACIÓN PNG CON MÚLTIPLES MÉTODOS ===');
        
        // Método 1: Exportación directa SVG (más confiable para Google Charts)
        result = await exportSVGDirectToPNG(elementRef.current, uniqueFileName);
        
        // Si falla, intentar método con html2canvas normal
        if (!result?.success) {
          console.log('Método directo SVG falló, intentando html2canvas normal...');
          result = await exportToPNG(
            elementRef.current, 
            uniqueFileName,
            config.png?.canvas || {}
          );
        }
        
        // Si aún falla, intentar método alternativo con contenedor temporal
        if (!result?.success) {
          console.log('Método normal falló, intentando método con contenedor temporal...');
          result = await exportGoogleChartToPNG(
            elementRef.current,
            uniqueFileName,
            config.png?.canvas || {}
          );
        }
      } else if (format === 'pdf') {
        console.log('=== INICIANDO EXPORTACIÓN PDF CON MÚLTIPLES MÉTODOS ===');
        
        const pdfConfig = {
          ...config.pdf,
          title: title || config.pdf?.title || 'Gráfica Exportada'
        };
        
        // Método 1: Exportación directa SVG a PDF (más confiable)
        result = await exportSVGDirectToPDF(elementRef.current, uniqueFileName, pdfConfig);
        
        // Si falla, intentar método con html2canvas normal
        if (!result?.success) {
          console.log('Método directo SVG para PDF falló, intentando html2canvas normal...');
          result = await exportToPDF(elementRef.current, uniqueFileName, pdfConfig);
        }
        
        // Si aún falla, intentar método con contenedor temporal
        if (!result?.success) {
          console.log('Método normal para PDF falló, intentando contenedor temporal...');
          result = await exportGoogleChartToPDF(elementRef.current, uniqueFileName, pdfConfig);
        }
      }

      if (result?.success) {
        message.success(`${result.message} (${format.toUpperCase()})`);
      } else {
        message.error(result?.message || `Error al exportar ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error(`Error en exportación ${format}:`, error);
      message.error(`Error inesperado al exportar ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
      setExportingType(null);
    }
  };

  // Opciones del dropdown
  const exportOptions = [
    {
      key: 'png',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileImageOutlined style={{ color: '#52c41a' }} />
          <span>Exportar como PNG</span>
        </div>
      ),
      onClick: () => handleExport('png'),
      disabled: isExporting
    },
    {
      key: 'pdf',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FilePdfOutlined style={{ color: '#ff4d4f' }} />
          <span>Exportar como PDF</span>
        </div>
      ),
      onClick: () => handleExport('pdf'),
      disabled: isExporting
    }
  ];

  // Botones individuales con diseño elegante
  const renderIndividualButtons = () => (
    <div style={{ display: 'flex', gap: '6px', ...style }}>
      <Tooltip title="Descargar como PNG" placement="bottom">
        <button
          onClick={() => handleExport('png')}
          disabled={isExporting}
          style={{
            background: isExporting && exportingType === 'png' 
              ? 'linear-gradient(135deg, #95de64 0%, #73d13d 100%)'
              : 'linear-gradient(135deg, #73d13d 0%, #52c41a 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            color: 'white',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            fontWeight: '500',
            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
            transition: 'all 0.2s ease',
            opacity: isExporting && exportingType !== 'png' ? 0.6 : 1,
            minWidth: '50px',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => {
            if (!isExporting) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!isExporting) {
              e.target.style.transform = '';
              e.target.style.boxShadow = '0 2px 8px rgba(82, 196, 26, 0.3)';
            }
          }}
          onFocus={(e) => {
            if (!isExporting) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.4)';
            }
          }}
          onBlur={(e) => {
            if (!isExporting) {
              e.target.style.transform = '';
              e.target.style.boxShadow = '0 2px 8px rgba(82, 196, 26, 0.3)';
            }
          }}
        >
          {isExporting && exportingType === 'png' ? (
            <span style={{
              width: '14px',
              height: '14px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
          ) : (
            <>
              <FileImageOutlined style={{ fontSize: '14px' }} />
              {showLabels && 'PNG'}
            </>
          )}
        </button>
      </Tooltip>

      <Tooltip title="Descargar como PDF" placement="bottom">
        <button
          onClick={() => handleExport('pdf')}
          disabled={isExporting}
          style={{
            background: isExporting && exportingType === 'pdf' 
              ? 'linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%)'
              : 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            color: 'white',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            fontWeight: '500',
            boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
            transition: 'all 0.2s ease',
            opacity: isExporting && exportingType !== 'pdf' ? 0.6 : 1,
            minWidth: '50px',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => {
            if (!isExporting) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!isExporting) {
              e.target.style.transform = '';
              e.target.style.boxShadow = '0 2px 8px rgba(255, 77, 79, 0.3)';
            }
          }}
          onFocus={(e) => {
            if (!isExporting) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.4)';
            }
          }}
          onBlur={(e) => {
            if (!isExporting) {
              e.target.style.transform = '';
              e.target.style.boxShadow = '0 2px 8px rgba(255, 77, 79, 0.3)';
            }
          }}
        >
          {isExporting && exportingType === 'pdf' ? (
            <span style={{
              width: '14px',
              height: '14px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
          ) : (
            <>
              <FilePdfOutlined style={{ fontSize: '14px' }} />
              {showLabels && 'PDF'}
            </>
          )}
        </button>
      </Tooltip>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Dropdown con opciones
  const renderDropdownButton = () => (
    <Dropdown 
      menu={{ items: exportOptions }} 
      placement="bottomRight"
      trigger={['click']}
      disabled={isExporting}
    >
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={isExporting}
        size={size}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          ...style
        }}
      >
        {showLabels && 'Exportar'}
      </Button>
    </Dropdown>
  );

  // Retornar botones individuales por defecto, dropdown si se especifica
  return style.variant === 'dropdown' ? renderDropdownButton() : renderIndividualButtons();
};

ExportChartButtons.propTypes = {
  elementRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  fileName: PropTypes.string,
  chartType: PropTypes.oneOf(['sankey', 'bar', 'line', 'pie']),
  title: PropTypes.string,
  analysisType: PropTypes.oneOf(['proyecto', 'elemento', 'tarjeta', '']),
  analysisName: PropTypes.string,
  showLabels: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  style: PropTypes.shape({
    variant: PropTypes.oneOf(['individual', 'dropdown'])
  })
};

export default ExportChartButtons;